import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  SmartToy as AgentIcon,
  MenuBook as KBIcon,
  Logout as LogoutIcon,
  Chat as ChatIcon,
  RateReview as SuggestionsIcon,
  Business as CRMIcon,
  Campaign as CampaignIcon,
  CloudDownload as BackupIcon,
  DeleteSweep as DataIcon,
  Stars as PMVVIcon,
  Link as WorkspaceIcon,
  AssistantOutlined as AssistantIcon,
  Inbox as InboxIcon,
  TrendingUp as FinanceIcon,
  Schedule as ScheduleIcon,
  Insights as InsightsIcon,
} from "@mui/icons-material";
import { Badge } from "@mui/material";
import { useFeedbackInbox } from "@/hooks/useFeedback";
import LinkedInQuickAddFab from "@/components/LinkedInQuickAddFab";

const DRAWER_WIDTH = 260;

const mainNavItems = [
  { label: "Dashboard", icon: <DashboardIcon />, path: "/" },
  { label: "My Assistant", icon: <AssistantIcon />, path: "/my-assistant" },
  { label: "Agents", icon: <AgentIcon />, path: "/agents" },
  { label: "Knowledge Base", icon: <KBIcon />, path: "/kb" },
  { label: "CRM", icon: <CRMIcon />, path: "/crm" },
  { label: "Campaigns", icon: <CampaignIcon />, path: "/campaigns" },
  { label: "Finance", icon: <FinanceIcon />, path: "/finance" },
  { label: "Insights", icon: <InsightsIcon />, path: "/insights" },
];

const settingsNavItems = [
  { label: "Purpose, Mission, Vision, Values", icon: <PMVVIcon />, path: "/settings/pmvv" },
  { label: "Scheduled Prompts", icon: <ScheduleIcon />, path: "/settings/schedules" },
  { label: "Google Workspace", icon: <WorkspaceIcon />, path: "/settings/workspace" },
];

const adminNavItems = [
  { label: "Feedback Inbox", icon: <InboxIcon />, path: "/admin/feedback" },
  { label: "Sandbox", icon: <ChatIcon />, path: "/sandbox" },
  { label: "Suggestions", icon: <SuggestionsIcon />, path: "/admin/suggestions" },
  { label: "Backup", icon: <BackupIcon />, path: "/admin/backup" },
  { label: "Data Management", icon: <DataIcon />, path: "/admin/data" },
];

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  // Count open (new + triaged) feedback items so the sidebar entry can
  // show a badge — turns the inbox into a glance-able backlog indicator.
  const { data: feedbackInbox } = useFeedbackInbox();
  const openFeedbackCount = (feedbackInbox?.items ?? []).filter(
    (i) => i.triage_status === "new" || i.triage_status === "triaged",
  ).length;

  const renderNavItems = (items: typeof mainNavItems) =>
    items.map((item) => {
      const showBadge = item.path === "/admin/feedback" && openFeedbackCount > 0;
      return (
        <ListItemButton
          key={item.path}
          selected={location.pathname === item.path}
          onClick={() => {
            navigate(item.path);
            setMobileOpen(false);
          }}
        >
          <ListItemIcon>
            {showBadge ? (
              <Badge badgeContent={openFeedbackCount} color="warning">
                {item.icon}
              </Badge>
            ) : (
              item.icon
            )}
          </ListItemIcon>
          <ListItemText primary={item.label} />
        </ListItemButton>
      );
    });

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar>
        <Typography variant="h6" sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
          Mastermind
        </Typography>
      </Toolbar>
      <List>{renderNavItems(mainNavItems)}</List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0.5,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 1,
        }}
      >
        Settings
      </Typography>
      <List dense>{renderNavItems(settingsNavItems)}</List>
      <Divider />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0.5,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 1,
        }}
      >
        Admin
      </Typography>
      <List dense>{renderNavItems(adminNavItems)}</List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
          >
            Mastermind
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {user && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {user.displayName ?? user.email}
              </Typography>
              <IconButton color="inherit" onClick={signOut} size="small" title="Sign out">
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          ml: { md: `${DRAWER_WIDTH}px` },
          mt: "64px",
          backgroundColor: "background.default",
        }}
      >
        <Outlet />
      </Box>

      {/* Global LinkedIn quick-add FAB — only when signed in, so it
          doesn't appear on the sign-in screen. */}
      {user && <LinkedInQuickAddFab />}
    </Box>
  );
}
